import { type GroupMemberCounts, getGroupLabel, getGroupThumbnailUrl } from '../../tokens/mediaFormatUtils';
import { type CanvasTokenNode } from '../slices/createCanvasTokenNodeSlice';
import { findGroupMembers } from './groupUtils';

export type GroupedTokenDisplay = {
  groupId: string;
  memberCount: number;
  name: string;
  thumbnailUrl: string | null;
};

/**
 * The same asset can sit in the node maps under two keys (a local id and its remote id, or a
 * toolbar `sourceNodeId` alias) that share one `group.id`. Collapse only that alias pair — not
 * distinct layers that happen to reuse the same source file.
 */
const withoutSourceNodeIdAlias = (
  members: [string, CanvasTokenNode][],
  groupId: string,
): [string, CanvasTokenNode][] => {
  const canonical = members.find(([key]) => key === groupId);

  if (!canonical) {
    return members;
  }

  const canonicalNode = canonical[1];

  return members.filter(([key, node]) => {
    if (
      key !== groupId &&
      (node.type === 'image' || node.type === 'video') &&
      (canonicalNode.type === 'image' || canonicalNode.type === 'video') &&
      node.type === canonicalNode.type &&
      node.media.original === canonicalNode.media.original
    ) {
      return false;
    }

    return true;
  });
};

export const tryResolveMultiMemberGroupDisplay = (
  nodes: Record<string, CanvasTokenNode>,
  groupId: string,
  thumbnailSize: number,
): GroupedTokenDisplay | null => {
  const members = withoutSourceNodeIdAlias(findGroupMembers(nodes, groupId), groupId);

  if (members.length < 2) {
    return null;
  }

  const memberNodes = members.map(([, node]) => node);
  const counts: GroupMemberCounts = { image: 0, text: 0, video: 0 };

  for (const node of memberNodes) {
    if (node.type === 'image' || node.type === 'text' || node.type === 'video') {
      counts[node.type]++;
    }
  }

  return {
    groupId,
    memberCount: members.length,
    name: getGroupLabel(counts),
    thumbnailUrl: getGroupThumbnailUrl(memberNodes, thumbnailSize),
  };
};

export type ResolvedGroupedTokenDisplay =
  | { type: 'media'; id: string; node: CanvasTokenNode }
  | { type: 'group'; groupId: string; memberCount: number; name: string; thumbnailUrl: string | null };

/**
 * Resolves a token id to either a single media node or a collapsed multi-member group chip.
 * Individual layer ids from split-element results map to the grouped flat asset when possible.
 */
export const resolveGroupedTokenDisplay = (
  nodes: Record<string, CanvasTokenNode>,
  id: string,
  thumbnailSize: number,
): ResolvedGroupedTokenDisplay | null => {
  const node = nodes[id];

  if (node) {
    const groupId = node.group?.id;

    if (groupId) {
      const group = tryResolveMultiMemberGroupDisplay(nodes, groupId, thumbnailSize);

      if (group) {
        return {
          groupId: group.groupId,
          memberCount: group.memberCount,
          name: group.name,
          thumbnailUrl: group.thumbnailUrl,
          type: 'group',
        };
      }
    }

    return { id, node, type: 'media' };
  }

  const group = tryResolveMultiMemberGroupDisplay(nodes, id, thumbnailSize);

  if (group) {
    return {
      groupId: group.groupId,
      memberCount: group.memberCount,
      name: group.name,
      thumbnailUrl: group.thumbnailUrl,
      type: 'group',
    };
  }

  return null;
};
